import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { InputText } from 'primereact/inputtext';
import { BreadCrumb } from 'primereact/breadcrumb';

const ShopCategory = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const categories = {
    'membership-upgrade': {
      id: 'membership-upgrade',
      name: 'Membership Upgrade',
      description: 'Upgrade your membership to access exclusive benefits',
      icon: 'pi pi-star',
      color: 'primary'
    },
    'clothing-merchandise': {
      id: 'clothing-merchandise',
      name: 'Clothing & Merchandise',
      description: 'Official Sport Endurance branded clothing and accessories',
      icon: 'pi pi-shopping-bag',
      color: 'secondary'
    },
    'gift-tokens': {
      id: 'gift-tokens',
      name: 'Gift Tokens',
      description: 'Gift vouchers for rides, events, and merchandise',
      icon: 'pi pi-gift',
      color: 'tertiary'
    },
    'hivis-clothing': {
      id: 'hivis-clothing',
      name: 'HiVis Clothing',
      description: 'High visibility safety clothing for rides',
      icon: 'pi pi-shield',
      color: 'warning'
    },
    'trailer-car-stickers': {
      id: 'trailer-car-stickers',
      name: 'Trailer & Car Stickers',
      description: 'Show your Sport Endurance pride on the road',
      icon: 'pi pi-car',
      color: 'info'
    }
  };

  const allProducts = [
    {
      id: 'henbury-polo',
      name: 'Henbury Ladies Polo HB401 with logo',
      price: 22.50,
      description: 'Henbury HB401 Women\'s 65/35 polo with SE logo',
      image: '/api/placeholder/300/300',
      category: 'clothing-merchandise',
      inStock: true
    },
    {
      id: 'gift-voucher-rides',
      name: 'Gift voucher for on-line orders',
      price: 25.00,
      description: 'Credit Vouchers to give to your friends!',
      image: '/api/placeholder/300/300',
      category: 'gift-tokens',
      inStock: true
    },
    {
      id: 'unisex-hoodie',
      name: 'Unisex hoodie JH001 with logo and website on back',
      price: 35.00,
      description: 'Twin needle stitching detailing. Double fabric hood.',
      image: '/api/placeholder/300/300',
      category: 'clothing-merchandise',
      inStock: true
    },
    {
      id: 'baseball-cap',
      name: 'Baseball Cap Ultimate 5 panel cap BC015 with logo',
      price: 18.00,
      description: 'Contemporary styling with SE logo',
      image: '/api/placeholder/300/300',
      category: 'clothing-merchandise',
      inStock: true
    },
    {
      id: 'premium-membership',
      name: 'Premium Membership Upgrade',
      price: 50.00,
      description: 'Upgrade to premium membership for exclusive benefits',
      image: '/api/placeholder/300/300',
      category: 'membership-upgrade',
      inStock: true
    },
    {
      id: 'elite-membership',
      name: 'Elite Membership Upgrade',
      price: 100.00,
      description: 'Our highest tier membership with all benefits',
      image: '/api/placeholder/300/300',
      category: 'membership-upgrade',
      inStock: true
    },
    {
      id: 'hi-vis-vest',
      name: 'High Visibility Safety Vest',
      price: 28.00,
      description: 'EN471 compliant hi-vis vest for safety',
      image: '/api/placeholder/300/300',
      category: 'hivis-clothing',
      inStock: true
    },
    {
      id: 'window-sticker',
      name: 'SE Window Sticker',
      price: 5.00,
      description: 'Show your Sport Endurance pride',
      image: '/api/placeholder/300/300',
      category: 'trailer-car-stickers',
      inStock: true
    }
  ];

  const category = categories[categoryId];
  const categoryProducts = allProducts.filter(product => product.category === categoryId);

  const filteredProducts = categoryProducts.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const breadcrumbItems = [
    { label: 'Shop', command: () => navigate('/shop') },
    { label: category ? category.name : 'Category' }
  ];

  if (!category) {
    return (
      <div>
        <div className="text-center">
          <h2>Category Not Found</h2>
          <p>The requested category could not be found.</p>
          <Button 
            label="Back to Shop" 
            icon="pi pi-arrow-left" 
            onClick={() => navigate('/shop')}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <BreadCrumb model={breadcrumbItems} className="mb-4" />

      {/* Category Header */}
      <div className="se-card mb-4">
        <div className="se-card-content">
          <div className="flex align-items-center gap-3 mb-3">
            <div className={`inline-flex align-items-center justify-content-center border-circle w-4rem h-4rem se-bg-${category.color}`}>
              <i className={`${category.icon} text-2xl text-white`}></i>
            </div>
            <div>
              <h1 className="mt-0 mb-1">{category.name}</h1>
              <p className="m-0 text-600">{category.description}</p>
            </div>
          </div>
          
          <div className="flex justify-content-between align-items-center">
            <Badge value={`${categoryProducts.length} products`} severity="info" />
            <div className="flex align-items-center gap-2">
              <i className="pi pi-search"></i>
              <InputText
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search products..."
                className="w-20rem"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length > 0 ? (
        <div className="grid">
          {filteredProducts.map((product) => (
            <div key={product.id} className="col-12 sm:col-6 lg:col-4 xl:col-3">
              <Card 
                className="h-full cursor-pointer hover:shadow-3 transition-all transition-duration-200"
                onClick={() => navigate(`/shop/product/${product.id}`)}
              >
                <div className="text-center mb-3">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full border-round"
                    style={{ height: '200px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDMwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjhGOUZBIi8+CjxwYXRoIGQ9Ik0xNTAgMTA1TDE5NSAxNTBIMTA1TDE1MCAxMDVaIiBmaWxsPSIjREVFMkU2Ii8+CjxjaXJjbGUgY3g9IjE1MCIgY3k9IjE1MCIgcj0iNjAiIHN0cm9rZT0iI0RFRTJFNiIgc3Ryb2tlLXdpZHRoPSIzIiBmaWxsPSJub25lIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMjEwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNjY3MDdCIiBmb250LXNpemU9IjE4Ij5Qcm9kdWN0PC90ZXh0Pgo8L3N2Zz4K';
                    }}
                  />
                </div>
                
                <h4 className="mt-0 mb-2 line-height-3">{product.name}</h4>
                <p className="text-600 text-sm mb-3 line-height-3">{product.description}</p>
                
                <div className="flex justify-content-between align-items-center">
                  <div className="text-xl font-bold se-text-primary">£{product.price.toFixed(2)}</div>
                  {product.inStock ? (
                    <Badge value="In Stock" severity="success" />
                  ) : (
                    <Badge value="Out of Stock" severity="danger" />
                  )}
                </div>
              </Card>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="text-6xl text-300 mb-3">
            <i className="pi pi-search"></i>
          </div>
          <h3 className="text-600">No products found</h3>
          <p className="text-500 mb-4">
            {searchTerm ? 
              `No products match "${searchTerm}" in this category.` : 
              'This category doesn\'t have any products yet.'
            }
          </p>
          {searchTerm && (
            <Button 
              label="Clear Search" 
              icon="pi pi-times" 
              className="p-button-outlined"
              onClick={() => setSearchTerm('')}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ShopCategory;